import React from 'react';
import { motion } from 'framer-motion';
import { GlassCard } from './glass-card';
import { AnimatedCounter } from './animated-counter';

interface DataPoint {
  label: string;
  value: number;
  color: string;
  icon?: React.ReactNode;
}

interface DataVisualizationProps {
  title: string;
  data: DataPoint[];
  total: number;
  className?: string;
}

export const DataVisualization: React.FC<DataVisualizationProps> = ({
  title,
  data,
  total,
  className = ''
}) => {
  return (
    <GlassCard className={`p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">{title}</h3>
      
      <div className="space-y-4">
        {data.map((item, index) => {
          const percentage = (item.value / total) * 100;
          
          return (
            <motion.div
              key={item.label}
              className="relative"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {item.icon && (
                    <div className="w-4 h-4 flex items-center justify-center">
                      {item.icon}
                    </div>
                  )}
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {item.label}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <AnimatedCounter
                    value={item.value}
                    className="text-sm font-semibold text-gray-900 dark:text-white"
                    duration={1.5}
                  />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    ({percentage.toFixed(1)}%)
                  </span>
                </div>
              </div>
              
              <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <motion.div
                  className="absolute top-0 left-0 h-full rounded-full"
                  style={{ backgroundColor: item.color }}
                  initial={{ width: 0 }}
                  animate={{ width: `${percentage}%` }}
                  transition={{ delay: index * 0.1 + 0.3, duration: 1, ease: "easeOut" }}
                />
                
                {/* Shimmer effect */}
                <motion.div
                  className="absolute top-0 left-0 h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{
                    delay: index * 0.1 + 1.3,
                    duration: 0.8,
                    ease: "easeInOut"
                  }}
                />
              </div>
            </motion.div>
          );
        })}
      </div>
    </GlassCard>
  );
};